export const TAX_PROFILE_PLAYS = [
  { id: 'rd', label: 'R&D and innovation', description: 'Technical activity and investment' },
  { id: 'capital_allowances', label: 'Capital allowances', description: 'Property and asset investment' },
  { id: 'accounts_audit', label: 'Accounts and audit', description: 'Accounts calendar and advisers' },
  { id: 'energy', label: 'Energy tax', description: 'Operations and recorded energy use' },
  { id: 'international_tax', label: 'International tax', description: 'Group, geographic revenue and trade context' },
] as const;

export type TaxProfilePlay = typeof TAX_PROFILE_PLAYS[number]['id'];

export interface TaxProfileFact {
  label: string;
  value: string;
  period?: string;
  sourceUrl?: string;
  sourceLabel: string;
  note?: string;
}

export interface TaxProfileArticle {
  title: string;
  date?: string;
  source: string;
  url?: string;
  kind: 'press' | 'company';
  summary?: string;
}

export interface TaxProfilePerson {
  name: string;
  roles: string[];
  appointedOn?: string;
  informationUrl?: string;
  linkedInUrl?: string;
  linkedInIsDirect: boolean;
  nationalities: string;
  nationalityCodes: string[];
  age?: number;
}

export function isTaxProfilePlay(value: string | null): value is TaxProfilePlay {
  return TAX_PROFILE_PLAYS.some(play => play.id === value);
}

export function taxExternalUrl(value: unknown): string | undefined {
  const url = typeof value === 'string' ? value.trim() : '';
  return /^https?:\/\//i.test(url) ? url : undefined;
}

export function taxProfilePeriodLabel(period: string): string {
  // Accounts columns can have a descriptive label rather than an ISO date.
  // Preserve those labels and format only complete, valid dates.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(period)) return period;
  const date = new Date(`${period}T00:00:00Z`);
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== period) return period;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  }).format(date);
}

function validDate(value: unknown): string | undefined {
  const date = typeof value === 'string' ? value.trim() : '';
  return date && Number.isFinite(Date.parse(date)) ? date : undefined;
}

function presentValue(value: unknown, type = 'funds'): string {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (value !== null && value !== '' && Number.isFinite(numeric)) {
    return type === 'funds'
      ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(numeric)
      : new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(numeric);
  }
  return String(value ?? '');
}

function accountColumns(company: Record<string, any>): Record<string, any>[] {
  const accounts = company?.financials?.accounts || {};
  return Object.values(accounts).find((section: any) => Array.isArray(section?.columns))?.['columns'] || [];
}

function sourceForPeriod(company: Record<string, any>, period: string): string | undefined {
  const periods = company?.financials?.made_up_to_dates || [];
  const index = periods.indexOf(period);
  return index >= 0 ? taxExternalUrl(accountColumns(company)[index]?.url_to_pdf_file) : undefined;
}

export function accountFact(company: Record<string, any>, name: string, label = name, note?: string): TaxProfileFact | null {
  const accounts = company?.financials?.accounts || {};
  const periods: string[] = company?.financials?.made_up_to_dates || [];
  for (const section of Object.values(accounts) as any[]) {
    const row = (section?.rows || []).find((candidate: any) => String(candidate?.name || '').toLowerCase() === name.toLowerCase());
    if (!row) continue;
    for (let index = 0; index < (section?.columns || []).length; index++) {
      const column = section.columns[index];
      const value = row.data?.[column.column_name];
      if (value === null || value === undefined || value === '') continue;
      const sourceUrl = taxExternalUrl(column.url_to_pdf_file);
      return {
        label,
        value: presentValue(value, row.type),
        period: periods[index] || column.column_name,
        sourceUrl,
        sourceLabel: sourceUrl ? 'View filed accounts' : 'Accounts extract',
        note,
      };
    }
  }
  return null;
}

export function assetAdditionFact(company: Record<string, any>, key: string, label: string): TaxProfileFact | null {
  for (const period of Object.keys(company?.asset_breakdown || {}).sort().reverse()) {
    const value = company.asset_breakdown[period]?.tangible?.[key];
    if (value === null || value === undefined || value === '') continue;
    const sourceUrl = sourceForPeriod(company, period);
    return {
      label,
      value: presentValue(value),
      period,
      sourceUrl,
      sourceLabel: sourceUrl ? 'View filed accounts' : 'Accounts asset extract',
      note: 'Recorded asset addition; this is not an allowance or claim amount.',
    };
  }
  return null;
}

export function taxProfileArticles(company: Record<string, any>): TaxProfileArticle[] {
  const news = company?.latest_news || {};
  const mapped: TaxProfileArticle[] = [
    ...(news.news || []).map((item: any) => ({
      title: String(item.title || '').trim(),
      date: validDate(item.published_date),
      source: String(item.source || 'In the press'),
      url: taxExternalUrl(item.article_link),
      kind: 'press' as const,
      summary: String(item.description || '').trim(),
    })),
    ...(news.self_published_news || []).map((item: any) => ({
      title: String(item.article_title || item.title || '').trim(),
      date: validDate(item.article_published_date),
      source: 'Company update',
      url: taxExternalUrl(item.url),
      kind: 'company' as const,
      summary: String(item.article_description || '').trim(),
    })),
  ];
  const seen = new Set<string>();
  return mapped.filter(article => {
    const key = article.url || article.title.toLowerCase();
    if (!article.title || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).sort((a, b) => (Date.parse(b.date || '') || 0) - (Date.parse(a.date || '') || 0));
}

const PLAY_TERMS: Record<TaxProfilePlay, RegExp> = {
  rd: /\b(research|development|innovation|patent|product|technology|engineering|software|grant)\b/i,
  capital_allowances: /\b(property|premises|refurbish\w*|building\w*|factory|plant|equipment|investment|acquisition|fit.out)\b/i,
  accounts_audit: /\b(acquisition|funding|appoint|finance|auditor|accountant|growth|expansion)\b/i,
  energy: /\b(energy|electric\w*|gas|manufactur\w*|alumini\w*|extrusion|power|emission\w*)\b/i,
  international_tax: /\b(overseas|international|global|export\w*|import\w*|cross.border|foreign|subsidiar\w*|transfer.pric\w*)\b/i,
};

function topicalArticleFact(company: Record<string, any>, play: TaxProfilePlay): TaxProfileFact | null {
  const article = taxProfileArticles(company).find(item => PLAY_TERMS[play].test(item.title + ' ' + (item.summary || '')));
  return article ? {
    label: 'Recent topical mention',
    value: article.title,
    period: article.date,
    sourceUrl: article.url,
    sourceLabel: article.source,
    note: 'A mention to review, not evidence of tax eligibility.',
  } : null;
}

function patentFact(company: Record<string, any>): TaxProfileFact | null {
  const patent = (company?.patents_filed || []).find((item: any) => item.title);
  return patent ? {
    label: 'Recorded patent filing',
    value: patent.title,
    period: patent.date_filed || patent.date_lodged || undefined,
    sourceUrl: taxExternalUrl(patent.ipo_link),
    sourceLabel: 'Patent record',
  } : null;
}

function grantFact(company: Record<string, any>): TaxProfileFact | null {
  const grant = (company?.grants || []).find((item: any) => item.project_title);
  return grant ? {
    label: 'Recorded grant project',
    value: grant.project_title,
    period: grant.project_start_date || undefined,
    sourceLabel: 'Grant record',
  } : null;
}

function energyFact(company: Record<string, any>, key: string, label: string): TaxProfileFact | null {
  const energy = (company?.energy_and_emissions?.energy || []).find((item: any) => item[key] !== null && item[key] !== undefined);
  if (!energy) return null;
  const period = energy.made_up_to || undefined;
  const sourceUrl = period ? sourceForPeriod(company, period) : undefined;
  return {
    label,
    value: presentValue(energy[key], 'number') + ' kWh',
    period,
    sourceUrl,
    sourceLabel: sourceUrl ? 'View filed accounts' : 'Recorded energy data',
    note: 'Recorded consumption; relief eligibility has not been assessed.',
  };
}

function groupConnectionFact(company: Record<string, any>): TaxProfileFact | null {
  return company?.company_overview?.is_in_group === true ? {
    label: 'Recorded group connection',
    value: 'Present',
    sourceLabel: 'Corporate structure record',
    note: 'A parent or child relationship is recorded. This does not identify the immediate parent or its country.',
  } : null;
}

function beneficialOwnerCountryFact(company: Record<string, any>): TaxProfileFact | null {
  const countries = [...new Set((company?.company_overview?.ubos || [])
    .map((owner: any) => String(owner?.country_name || '').trim())
    .filter(Boolean))].slice(0, 3);
  return countries.length ? {
    label: 'Recorded beneficial owner jurisdiction',
    value: countries.join(', '),
    sourceLabel: 'Beneficial ownership extract',
    note: 'An owner jurisdiction is not evidence of an immediate parent company or connected-party trading.',
  } : null;
}

function geographicalRevenueFacts(company: Record<string, any>): TaxProfileFact[] {
  const seen = new Set<string>();
  return (company?.geographical_revenue || [])
    .filter((item: any) => {
      const name = String(item?.name || '').trim();
      const key = name.toLowerCase();
      if (!name || seen.has(key) || item.revenue === null || item.revenue === undefined ||
          item.revenue === '' || !Number.isFinite(Number(item.revenue))) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 2)
    .map((item: any) => ({
      label: `Revenue attributed to ${String(item.name).trim()}`,
      value: presentValue(item.revenue),
      sourceLabel: 'Geographical revenue extract',
      note: 'The source period is not supplied here. Country and territory rows may overlap; this is not a foreign-revenue total.',
    }));
}

function tradeActivityFact(company: Record<string, any>, direction: 'imports' | 'exports'): TaxProfileFact | null {
  const months = company?.['imports_&_exports']?.[direction];
  if (!Array.isArray(months)) return null;
  const latest = months.slice().reverse().find((month: any) =>
    Number.isFinite(Number(month?.value)) && Number(month.value) > 0);
  if (!latest) return null;
  const count = Number(latest.value);
  return {
    label: direction === 'imports' ? 'Recorded imports' : 'Recorded exports',
    value: `${new Intl.NumberFormat('en-GB').format(count)} ${count === 1 ? 'event' : 'events'}`,
    period: String(latest.name || ''),
    sourceLabel: 'Trade event summary',
    note: 'Monthly activity count; it does not identify related-party transactions or establish a tax position.',
  };
}

export function taxProfileFacts(company: Record<string, any>, play: TaxProfilePlay): TaxProfileFact[] {
  const facts: Array<TaxProfileFact | null> = play === 'rd' ? [
    accountFact(company, 'Wages and Salaries', 'Wages and salaries'),
    accountFact(company, 'Tax Credit', 'Tax credit (accounts line)', 'This line alone does not establish whether an R&D claim was made.'),
    patentFact(company),
    grantFact(company),
    topicalArticleFact(company, play),
  ] : play === 'capital_allowances' ? [
    assetAdditionFact(company, 'land_and_buildings_additions', 'Land and buildings additions'),
    assetAdditionFact(company, 'plant_and_machinery_additions', 'Plant and machinery additions'),
    accountFact(company, 'Corporation Tax', 'Corporation tax (accounts line)', 'This accounts line is not a current tax liability or relief value.'),
    topicalArticleFact(company, play),
  ] : play === 'accounts_audit' ? [
    accountFact(company, 'Revenue'),
    accountFact(company, 'Profit Before Tax', 'Profit before tax'),
    accountFact(company, 'Cash At Bank', 'Cash at bank'),
    topicalArticleFact(company, play),
  ] : play === 'energy' ? [
    energyFact(company, 'total_energy_kwh', 'Total recorded energy'),
    energyFact(company, 'electric_kwh', 'Recorded electricity'),
    energyFact(company, 'gas_kwh', 'Recorded gas'),
    accountFact(company, 'EBITDA'),
    accountFact(company, 'Wages and Salaries', 'Wages and salaries'),
    topicalArticleFact(company, play),
  ] : [
    groupConnectionFact(company),
    beneficialOwnerCountryFact(company),
    ...geographicalRevenueFacts(company),
    tradeActivityFact(company, 'exports'),
    tradeActivityFact(company, 'imports'),
    accountFact(company, 'Gross Profit', 'Gross profit (accounts line)',
      'Review this alongside revenue for the same accounts period; it is not a transfer-pricing finding.'),
    accountFact(company, 'Revenue', 'Revenue (accounts line)'),
    topicalArticleFact(company, play),
  ];
  return facts.filter((fact): fact is TaxProfileFact => fact !== null)
    .slice(0, play === 'international_tax' ? 8 : 6);
}

export function taxProfilePeople(company: Record<string, any>): TaxProfilePerson[] {
  const people = (company?.key_people || []).map((person: any) => {
    const name = [person.forename, person.middle_name, person.surname].filter(Boolean).join(' ').trim() || person.name || '';
    const websiteRole = person.website_occupation ? String(person.website_occupation) : '';
    const occupation = Array.isArray(person.occupation) ? person.occupation.map(String) : [];
    const officerRoles = Array.isArray(person.officer_roles) ? person.officer_roles.map(String) : [];
    const roles = [...new Set([...officerRoles, websiteRole, ...occupation].filter(Boolean))];
    const nationalities = Array.isArray(person.nationalities) ? person.nationalities.filter(Boolean) : [];
    const nationalityCodes = Array.isArray(person.nationality_codes)
      ? person.nationality_codes.filter((code: unknown) => typeof code === 'string' && /^[a-z]{2}$/i.test(code))
        .map((code: string) => code.toLowerCase()) : [];
    let linkedInUrl: string | undefined;
    const rawLinkedInUrl = typeof person.linkedin_url === 'string' ? person.linkedin_url.trim() : '';
    if (rawLinkedInUrl) {
      try {
        const url = new URL(rawLinkedInUrl);
        if (['http:', 'https:'].includes(url.protocol) &&
          (url.hostname === 'linkedin.com' || url.hostname.endsWith('.linkedin.com'))) {
          url.protocol = 'https:';
          linkedInUrl = url.toString();
        }
      } catch { /* A malformed URL can still use the search fallback below. */ }
    }
    if (!linkedInUrl && person.forename && person.surname) {
      const keywords = [person.forename, person.surname, company?.company_overview?.name]
        .filter(Boolean).join(' ');
      linkedInUrl = `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(keywords)}`;
    }
    return {
      name,
      roles,
      allRoles: roles.join(' '),
      appointedOn: person.appointed_on || undefined,
      informationUrl: taxExternalUrl(person.information_url),
      linkedInUrl,
      linkedInIsDirect: !!person.is_direct_linkedin_url && !!linkedInUrl &&
        !linkedInUrl.includes('/search/results/people/'),
      nationalities: (nationalities.length ? nationalities : nationalityCodes).join(', '),
      nationalityCodes,
      age: Number.isFinite(Number(person.age)) && person.age !== null && person.age !== ''
        ? Number(person.age) : undefined,
    };
  }).filter((person: any) => person.name);
  const rank = (person: any) => /chief financial|finance director|financial director|head of finance|tax director|tax manager|cfo|\bfd\b/i.test(person.allRoles)
    ? 0 : /managing director|chief executive|\bceo\b|owner|founder/i.test(person.allRoles)
      ? 1 : /chief technology|technical|engineering|research|\bcto\b/i.test(person.allRoles) ? 2 : 3;
  return people.sort((a: any, b: any) => rank(a) - rank(b));
}
